import * as React from 'react';
import * as ReactDOM from 'react-dom';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { getMetaData } from '@helpcenter/meta';
import { IntlProvider } from 'react-intl';
import type { OnErrorFn } from '@formatjs/intl';
import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import { AnalyticsContext, ScreenTypes } from '@atlassian/analytics-web-react';
import { sendEvent } from '@atlassian/help-center-common-util/analytics';
import { getMessages } from '@atlassian/help-center-common-util/i18n-messages';
import { getLanguageFromLocale } from '@atlassian/help-center-common-util/locale-data';
import * as Sentry from '@atlassian/help-center-common-util/sentry';
import type { CmdbObject } from '../../../rest/cmdb-object-picker';
import { CmdbObjectCard } from '../../cmdb-object-card';

const onIntlError = (intlError: string): void => {
	try {
		Sentry.reportError(intlError);
	} catch (err) {
		// oxlint-disable-next-line no-console
		console.error(`Failed to report intl error: ${intlError}`, err);
	}
};

export const onIntlNextError: OnErrorFn = (intlError) => {
	onIntlError(intlError.message);
};

export const decodeBase64ToObject = (base64String: string | null): CmdbObject | null => {
	try {
		// Decode base64 string while preserving Unicode characters
		const binaryString = atob(base64String || '');
		const bytes = new Uint8Array(binaryString.length);
		for (let i = 0; i < binaryString.length; i++) {
			bytes[i] = binaryString.charCodeAt(i);
		}
		const decoder = new TextDecoder('utf-8');
		const decodedString = decoder.decode(bytes);
		return JSON.parse(decodedString) as CmdbObject;
	} catch (e) {
		return null;
	}
};

export const getCmdbFieldValueElement = (): CustomElementConstructor => {
	class CmdbFieldValueElement extends HTMLElement {
		connectedCallback() {
			// Jira passes the cmdb object data as a data-object-details-base64 attribute
			const objectDetailsBase64 = this.getAttribute('data-object-details-base64');
			const parsedObjectDetails = decodeBase64ToObject(objectDetailsBase64);

			if (!parsedObjectDetails) {
				return;
			}

			// render the component with its props, and add to the Shadow DOM
			// Upgrading @atlaskit/platform; fix these violations.
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			const root = document.createElement('span');
			// eslint-disable-next-line react/no-deprecated
			ReactDOM.render(
				<IntlProvider
					messages={getMessages()}
					locale={getLanguageFromLocale(getMetaData().userLocale)}
					onError={onIntlNextError}
				>
					<AnalyticsListener onEvent={sendEvent}>
						<AnalyticsContext
							sourceName="portalUnknown"
							sourceType={ScreenTypes.SCREEN}
							attributes={{ context: 'Portal' }}
						>
							<ErrorBoundary
								packageName="cmdbFieldValueWebComponent"
								id="cmdb-object-card"
								renderError={() => null}
							>
								<CmdbObjectCard cmdbObject={parsedObjectDetails} />
							</ErrorBoundary>
						</AnalyticsContext>
					</AnalyticsListener>
				</IntlProvider>,
				root,
			);
			this.appendChild(root);
		}
	}
	return CmdbFieldValueElement;
};
